import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  CardMedia,
  Skeleton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useGetBrands } from '../api/brands';
import PageHeader from '../components/main/PageHeader';
import SideMenu from '../components/main/SideMenu';
import type { Brand } from '../types/brand';
import { getBrandNameFromBrand, getBrandDescriptionFromBrand } from '../utils/productUtils';
import i18n from '@/i18n';

const CARD_WIDTH = 320;
const SKELETON_COUNT = 6;

export default function Brands() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { brandsData, brandsIsLoading } = useGetBrands();
  const [sideMenuOpen, setSideMenuOpen] = useState(false);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  // Listen for side menu open event from mobile bottom navigation
  useEffect(() => {
    const handleOpenSideMenu = () => {
      setSideMenuOpen(true);
    };

    window.addEventListener('openSideMenu', handleOpenSideMenu);
    
    return () => {
      window.removeEventListener('openSideMenu', handleOpenSideMenu);
    };
  }, []);

  const handleBrandClick = (brand: Brand) => {
    navigate(`/brand/${brand.id}/${encodeURIComponent(getBrandNameFromBrand(brand, i18n.language))}`);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fff' }}>
      {/* Header */}
      <PageHeader title={t('navigation.brands')} />

      {/* Brands Grid */}
      <Container maxWidth="lg" sx={{ py: 4, pt: { xs: 10, md: 4 } }}>
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          {(brandsIsLoading ? Array.from({ length: SKELETON_COUNT }) : brandsData).map(
            (item, idx) => (
              <Box
                key={brandsIsLoading ? idx : (item as Brand).id}
                onClick={() => !brandsIsLoading && handleBrandClick(item as Brand)}
                sx={{
                  minWidth: isMobile ? '42vw' : CARD_WIDTH,
                  maxWidth: isMobile ? '42vw' : CARD_WIDTH,
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid #E8E8E8',
                  borderBottom: `3px solid ${theme.palette.custom.brown}`,
                  position: 'relative',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out',
                  },
                }}
              >
                {brandsIsLoading ? (
                  <Skeleton
                    variant="rectangular"
                    width={CARD_WIDTH}
                    height={200}
                    sx={{ borderRadius: 3 }}
                  />
                ) : (
                  <CardMedia
                    component="img"
                    image={(item as Brand).imageUrl || '/images/img_placeholder.svg'}
                    alt={getBrandNameFromBrand(item as Brand, i18n.language)}
                    sx={{
                      width: '100%',
                      height: 200,
                      objectFit: 'cover',
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/images/img_placeholder.svg';
                    }}
                  />
                )}
                <Box
                  sx={{
                    flex: 1,
                    p: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderBottom: `2px solid ${theme.palette.custom.brown}`,
                  }}
                >
                  {brandsIsLoading ? (
                    <>
                      <Skeleton width="60%" height={28} sx={{ mb: 1 }} />
                      <Skeleton width="80%" height={18} />
                    </>
                  ) : (
                    <>
                      <Typography
                        fontWeight={700}
                        align="center"
                        sx={{ mb: 0.5 }}
                      >
                        {getBrandNameFromBrand(item as Brand, i18n.language)}
                      </Typography>
                      <Typography
                        color="text.secondary"
                        align="center"
                        sx={{ 
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          fontSize: '13px',
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {getBrandDescriptionFromBrand(item as Brand, i18n.language)}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            )
          )}
        </Box>
      </Container>

      {/* Side Menu */}
      <SideMenu 
        open={sideMenuOpen} 
        onClose={() => setSideMenuOpen(false)} 
      />
    </Box>
  );
}
